import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Truck } from "lucide-react";

import PaymentSummary from "./PaymentSummary";
import PaymentMethods from "./PaymentMethods";
import PaymentNotes from "./PaymentNotes";
import SupplierSelector from "./SupplierSelector";
import PaymentActions from "./PaymentActions";

import { usePayableTable } from "../../table/hooks/usePayableTable";
import { useApplyPayment } from "../hooks/useApplyPayment";

import {
  RootState,
  AppDispatch,
} from "../../../../store/store";

import {
  setPaymentDate,
  setNotes,
  setPayments,
} from "../store/PayablePayment.slice";

interface Props {
  PayableId?: number;
}

const PaymentForm = ({ PayableId }: Props) => {
  const dispatch: AppDispatch = useDispatch();

  const { fetchPaymentSummary } = usePayableTable();

  const {
    applyPayment,
    cancelPayment,
  } = useApplyPayment();

  /**
   * 🔥 leer todo desde redux
   */
  const {
    summary,
    paymentDate,
    notes,
    payments,
  } = useSelector(
    (state: RootState) => state.payablePayment
  );

  const isContextMode = !!PayableId;

  /**
   * 🔥 cargar cuenta desde backend → redux
   */
  useEffect(() => {
    if (!PayableId) return;

    fetchPaymentSummary(PayableId);
  }, [PayableId]);

  /**
   * 🔥 modo manual:
   * si no viene PayableId y no hay summary,
   * primero seleccionar Proveedor
   */
  if (!isContextMode && !summary) {
    return <SupplierSelector />;
  }

  /**
   * 🔥 submit centralizado
   */
  const handleSubmit = async () => {
    await applyPayment();
  };

  const paymentTotal = payments.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0
  );
  const isPaymentValid =
    payments.length > 0 &&
    payments.every(
      (payment) =>
        Boolean(payment.paymentMethodId) &&
        Number(payment.amount) > 0
    ) &&
    paymentTotal <= Number(summary?.balance || 0);

  return (
    <div>
      {/* 🔹 ENCABEZADO */}
      <div className="flex items-center gap-3 bg-danger/5 px-6 py-5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-danger text-white">
          <Truck className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-semibold text-black dark:text-white">
            Pagar a Proveedores
          </h2>
          <p className="text-sm text-body dark:text-bodydark2">
            Registra el pago de una cuenta por pagar
          </p>
        </div>
      </div>

      <div className="divide-y divide-stroke dark:divide-strokedark">
        {/* 🔹 RESUMEN + FECHA */}
        {summary && (
          <PaymentSummary
            data={summary}
            paymentDate={paymentDate}
            onPaymentDateChange={(value) =>
              dispatch(setPaymentDate(value))
            }
          />
        )}

        {/* 🔹 MÉTODOS */}
        <PaymentMethods
          items={payments}
          setItems={(value) =>
            dispatch(
              setPayments(
                typeof value === "function"
                  ? value(payments)
                  : value
              )
            )
          }
          balance={summary?.balance || 0}
        />

        {/* 🔹 NOTAS */}
        <PaymentNotes
          value={notes}
          onChange={(value) =>
            dispatch(setNotes(value))
          }
        />
      </div>

      {/* 🔹 ACTIONS */}
      <PaymentActions
        onSubmit={handleSubmit}
        onCancel={cancelPayment}
        isValid={isPaymentValid}
        isSubmitting={false}
      />
    </div>
  );
};

export default PaymentForm;
